"use client";

import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
    ArrowLeft,
    Check,
    ChevronRight,
    ChevronDown,
    Folder,
    Plus,
    Search,
    X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "@/core/redux/store";
import { specificationFoldersService } from "@/shared/services/api/specification-folders-service";
import { ISpecificationFolderItem } from "@/shared/services/interface/responses/ISpecificationFoldersResponse";
import { createSpecificationFolderThunk } from "@/core/redux/thunks/specification-folders-thunk";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { cn } from "@/lib/utils";

export interface FolderSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (folderId: string | null, specificationName?: string) => void;
    title?: string;
    description?: string;
    itemName?: string;
    actionLabel?: string;
    skipNameStep?: boolean;
}

interface FolderWithChildren extends ISpecificationFolderItem {
    children?: FolderWithChildren[];
    isLoading?: boolean;
    isExpanded?: boolean;
}

type FolderTreeApiItem = Partial<ISpecificationFolderItem> & {
    children?: FolderTreeApiItem[];
};

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}

// Step enum
type ModalStep = "folder" | "name" | "createFolder";

export default function FolderSelectionModal({
    isOpen,
    onClose,
    onSelect,
    title,
    description,
    itemName,
    actionLabel,
    skipNameStep = false,
}: FolderSelectionModalProps) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const me = useSelector((state) => state.user.me);

    // States
    const [folders, setFolders] = useState<FolderWithChildren[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
    const [selectedFolderName, setSelectedFolderName] = useState<string>("");
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState<"recent" | "all">("all");
    const [currentStep, setCurrentStep] = useState<ModalStep>("folder");
    const [newFolderName, setNewFolderName] = useState("");
    const [specificationName, setSpecificationName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
        new Set()
    );
    const [folderChildren, setFolderChildren] = useState<
        Record<string, FolderWithChildren[]>
    >({});
    const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set());

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const normalizeFolderTreeItem = useCallback(
        (item: FolderTreeApiItem): FolderWithChildren | null => {
            if (!item?.id || !item.name) return null;
            if (item.itemType && item.itemType !== "folder") return null;

            const normalizedChildren = Array.isArray(item.children)
                ? item.children
                    .map(normalizeFolderTreeItem)
                    .filter((child): child is FolderWithChildren => child !== null)
                : [];

            return {
                id: item.id,
                itemType: "folder",
                name: item.name,
                position: item.position || "0",
                createdAt: item.createdAt || "",
                updatedAt: item.updatedAt || "",
                hasChildItems:
                    typeof item.hasChildItems === "boolean"
                        ? item.hasChildItems
                        : normalizedChildren.length > 0,
                children: normalizedChildren,
            };
        },
        []
    );

    // Load root folders using getFolderTree API
    const loadRootFolders = useCallback(async () => {
        if (!me?.company?.id) return;

        setIsLoading(true);
        try {
            const sortParam = activeTab === "recent" ? "createdAt:DESC" : "name:ASC";

            const response = await specificationFoldersService.getFolderTree({
                companyId: me.company.id,
                offset: 0,
                limit: 50,
                sortBy: sortParam,
                search: debouncedSearchQuery || undefined,
            });

            const responseData = response.data as unknown;
            const rawItems = Array.isArray(responseData)
                ? (responseData as FolderTreeApiItem[])
                : Array.isArray((responseData as { items?: FolderTreeApiItem[] })?.items)
                    ? ((responseData as { items: FolderTreeApiItem[] }).items || [])
                    : [];

            const normalizedFolders = rawItems
                .map(normalizeFolderTreeItem)
                .filter((folder): folder is FolderWithChildren => folder !== null);

            setFolders(normalizedFolders);
        } catch (error) {
            console.error("Failed to load folders:", error);
        } finally {
            setIsLoading(false);
        }
    }, [me?.company?.id, debouncedSearchQuery, activeTab, normalizeFolderTreeItem]);

    // Load subfolders
    const loadSubfolders = useCallback(
        async (folderId: string) => {
            if (!me?.company?.id) return;

            setLoadingFolders((prev) => new Set(prev).add(folderId));
            try {
                const response = await specificationFoldersService.getFolderContents({
                    companyId: me.company.id,
                    folderId: folderId,
                    sortBy: "name",
                    order: "ASC",
                    offset: 0,
                    limit: 50,
                    filter: "all",
                });

                const folderItems = response.data.items.filter(
                    (item: ISpecificationFolderItem) => item.itemType === "folder"
                );
                setFolderChildren((prev) => ({
                    ...prev,
                    [folderId]: folderItems,
                }));
            } catch (error) {
                console.error("Failed to load subfolders:", error);
            } finally {
                setLoadingFolders((prev) => {
                    const next = new Set(prev);
                    next.delete(folderId);
                    return next;
                });
            }
        },
        [me?.company?.id]
    );

    // Toggle folder expansion
    const toggleFolderExpansion = useCallback(
        (folder: FolderWithChildren) => {
            setExpandedFolders((prev) => {
                const next = new Set(prev);
                if (next.has(folder.id)) {
                    next.delete(folder.id);
                } else {
                    next.add(folder.id);
                    const hasPreloadedChildren = (folder.children?.length || 0) > 0;
                    if (folder.hasChildItems && !folderChildren[folder.id] && !hasPreloadedChildren) {
                        loadSubfolders(folder.id);
                    }
                }
                return next;
            });
        },
        [folderChildren, loadSubfolders]
    );

    // Load folders when modal opens
    useEffect(() => {
        if (isOpen) {
            loadRootFolders();
            setSelectedFolderId(null);
            setSelectedFolderName("");
            setCurrentStep("folder");
            setNewFolderName("");
            setSpecificationName("");
            setExpandedFolders(new Set());
            setFolderChildren({});
        }
    }, [isOpen, loadRootFolders]);

    // Handle folder selection
    const handleFolderSelect = (folderId: string, folderName: string) => {
        if (selectedFolderId === folderId) {
            setSelectedFolderId(null);
            setSelectedFolderName("");
        } else {
            setSelectedFolderId(folderId);
            setSelectedFolderName(folderName);
        }
    };

    // Handle create new folder
    const handleCreateFolder = async () => {
        if (!me?.company?.id || !newFolderName.trim()) return;

        setIsCreatingFolder(true);
        try {
            const result = await dispatch(
                createSpecificationFolderThunk({
                    companyId: me.company.id,
                    name: newFolderName.trim(),
                    parentId: selectedFolderId || null,
                    description: "",
                })
            );

            if (createSpecificationFolderThunk.fulfilled.match(result)) {
                const payload = result.payload as { data?: { id?: string; name?: string } };
                const newFolderId = payload?.data?.id;
                const newFolderNameResult = payload?.data?.name || newFolderName.trim();
                if (newFolderId) {
                    setCurrentStep("folder");
                    setNewFolderName("");
                    setSelectedFolderId(newFolderId);
                    setSelectedFolderName(newFolderNameResult);
                    loadRootFolders();
                }
            }
        } catch (error) {
            console.error("Failed to create folder:", error);
        } finally {
            setIsCreatingFolder(false);
        }
    };

    // Handle proceed to name step (or skip if skipNameStep is true)
    const handleProceedToNameStep = () => {
        if (skipNameStep) {
            // Skip name step - directly create specification
            onSelect(selectedFolderId);
            onClose();
        } else {
            setCurrentStep("name");
        }
    };

    // Handle final action - create specification
    const handleFinalAction = () => {
        onSelect(selectedFolderId, specificationName.trim() || undefined);
        onClose();
    };

    // Render folder row (Figma design)
    const renderFolderRow = (
        folder: FolderWithChildren,
        depth: number = 0,
        isLast: boolean = false
    ): React.ReactNode => {
        const isSelected = selectedFolderId === folder.id;
        const isExpanded = expandedFolders.has(folder.id);
        const isLoadingChildren = loadingFolders.has(folder.id);
        const children = folderChildren[folder.id] || folder.children || [];
        const hasChildren = folder.hasChildItems || children.length > 0;

        return (
            <div key={folder.id}>
                {/* Row - 56px height as per Figma */}
                <div
                    className={cn(
                        "flex items-center h-[56px] cursor-pointer transition-colors",
                        "border-b border-[#E4E4E7]",
                        isSelected && "bg-[#F4F4F5]",
                        !isSelected && "hover:bg-[#FAFAFA]",
                        isLast && !isExpanded && depth === 0 && "border-b-0"
                    )}
                    onClick={() => handleFolderSelect(folder.id, folder.name)}
                >
                    {/* Chevron cell - 32px width */}
                    <div
                        className="flex items-center justify-center w-8 h-[56px] flex-shrink-0"
                        style={{ marginLeft: depth * 32 }}
                    >
                        {hasChildren ? (
                            <button
                                className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-zinc-200"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFolderExpansion(folder);
                                }}
                            >
                                {isLoadingChildren ? (
                                    <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
                                ) : isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-[#0A0A0A]" />
                                ) : (
                                    <ChevronRight className="w-4 h-4 text-[#0A0A0A]" />
                                )}
                            </button>
                        ) : null}
                    </div>

                    {/* Folder icon + name cell */}
                    <div className="flex items-center gap-2 flex-1 px-2 min-w-0 h-[56px]">
                        <Folder className="w-4 h-4 text-[#09090B] flex-shrink-0" />
                        <span className="text-sm font-medium text-[#09090B] truncate">
                            {folder.name}
                        </span>
                        {/* Child count badge */}
                        {hasChildren && (
                            <div className="flex items-center justify-center w-5 h-5 bg-[#F4F4F5] border border-[#E4E4E7] rounded ml-1">
                                <span className="text-xs text-[#71717A]">
                                    {children.length || "..."}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Selection indicator */}
                    {isSelected && (
                        <div className="flex items-center justify-center w-10 h-[56px] flex-shrink-0 pr-2">
                            <Check className="w-4 h-4 text-[#09090B]" />
                        </div>
                    )}
                </div>

                {/* Render children if expanded - with bg-[#F4F4F5] */}
                {isExpanded && children.length > 0 && (
                    <div className="bg-[#F4F4F5]">
                        {children.map((child, index) =>
                            renderFolderRow(child, depth + 1, index === children.length - 1)
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Step 3: Create folder view
    if (currentStep === "createFolder") {
        return (
            <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <AlertDialogContent
                    className="p-6 gap-4"
                    style={{ zIndex: 999, width: 600, maxWidth: 600 }}
                >
                    {/* Close button */}
                    <button
                        className="absolute top-[10px] right-[12px] p-[6px] rounded hover:bg-zinc-100"
                        onClick={onClose}
                    >
                        <X className="w-4 h-4 text-[#18181B]" />
                    </button>

                    <AlertDialogHeader className="gap-2">
                        <div className="flex items-center gap-2">
                            <button
                                className="p-1 hover:bg-zinc-100 rounded"
                                onClick={() => setCurrentStep("folder")}
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </button>
                            <AlertDialogTitle className="text-lg font-bold tracking-[-0.025em] text-[#09090B]">
                                {t("move-spec-to-new-folder")}
                            </AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm font-medium text-[#71717A]">
                            {t("create-new-folder-description")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="text-sm font-medium text-[#71717A] block mb-2">
                                {t("new-folder-name")}
                            </label>
                            <Input
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                placeholder={t("nameless-folder")}
                                className="h-10 border-[#E4E4E7]"
                                autoFocus
                            />
                        </div>

                        <div className="flex justify-end">
                            <Button
                                variant="default"
                                onClick={handleCreateFolder}
                                disabled={!newFolderName.trim() || isCreatingFolder}
                                className="font-bold min-w-20"
                            >
                                {isCreatingFolder
                                    ? t("creating") + "..."
                                    : t("add-new-folder")}
                            </Button>
                        </div>
                    </div>
                </AlertDialogContent>
            </AlertDialog>
        );
    }

    // Step 2: Specification name input
    if (currentStep === "name") {
        return (
            <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <AlertDialogContent
                    className="p-6 gap-4"
                    style={{ zIndex: 999, width: 600, maxWidth: 600 }}
                >
                    {/* Close button */}
                    <button
                        className="absolute top-[10px] right-[12px] p-[6px] rounded hover:bg-zinc-100"
                        onClick={onClose}
                    >
                        <X className="w-4 h-4 text-[#18181B]" />
                    </button>

                    <AlertDialogHeader className="gap-2">
                        <div className="flex items-center gap-2">
                            <button
                                className="p-1 hover:bg-zinc-100 rounded"
                                onClick={() => setCurrentStep("folder")}
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </button>
                            <AlertDialogTitle className="text-lg font-bold tracking-[-0.025em] text-[#09090B]">
                                {t("specification-name")}
                            </AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm font-medium text-[#71717A]">
                            <span className="text-[#71717A]">{t("selected-folder")}: </span>
                            <span className="text-[#09090B] font-semibold">{selectedFolderName}</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="text-sm font-medium text-[#71717A] block mb-2">
                                {t("specification-name")}
                            </label>
                            <Input
                                value={specificationName}
                                onChange={(e) => setSpecificationName(e.target.value)}
                                placeholder={t("enter-specification-name")}
                                className="h-10 border-[#E4E4E7]"
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => setCurrentStep("folder")}
                                className="flex-1 h-9 font-bold text-sm"
                            >
                                {t("back")}
                            </Button>
                            <Button
                                variant="default"
                                onClick={handleFinalAction}
                                disabled={!specificationName.trim()}
                                className={cn(
                                    "flex-1 h-9 font-bold text-sm",
                                    !specificationName.trim() && "opacity-50"
                                )}
                            >
                                {actionLabel || t("create")}
                            </Button>
                        </div>
                    </div>
                </AlertDialogContent>
            </AlertDialog>
        );
    }

    // Step 1: Main folder selection view
    return (
        <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <AlertDialogContent
                className="p-6 flex flex-col gap-4"
                style={{
                    zIndex: 999,
                    width: 600,
                    maxWidth: 600,
                    borderRadius: 12,
                    border: "1px solid #E4E4E7",
                    boxShadow: "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)"
                }}
            >
                {/* Close button - positioned as per Figma */}
                <button
                    className="absolute top-[10px] right-[12px] p-[6px] rounded hover:bg-zinc-100"
                    onClick={onClose}
                >
                    <X className="w-4 h-4 text-[#18181B]" />
                </button>

                {/* Header - AlertDialogContent */}
                <AlertDialogHeader className="gap-2 flex-shrink-0">
                    <AlertDialogTitle className="text-lg font-bold tracking-[-0.025em] text-[#09090B]">
                        {title || t("create-specification")}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-sm font-medium text-[#71717A]">
                        {description ||
                            (itemName
                                ? t("move-to-folder-description").replace("{name}", itemName)
                                : t("select-folder-description"))}
                    </AlertDialogDescription>
                </AlertDialogHeader>

                {/* Search Input - 40px height */}
                <div className="relative flex-shrink-0 h-10">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-[14px] h-[14px] text-[#09090B]" />
                    <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t("search-folders")}
                        className="pl-10 h-10 border-[#E4E4E7] text-sm font-medium placeholder:text-[#71717A]"
                    />
                </div>

                {/* Tabs - 40px height with 4px padding */}
                <Tabs
                    value={activeTab}
                    onValueChange={(v) => setActiveTab(v as "recent" | "all")}
                    className="flex-shrink-0"
                >
                    <TabsList className="w-full grid grid-cols-2 h-10 p-1 bg-[#F4F4F5] rounded-lg">
                        <TabsTrigger
                            value="recent"
                            className="text-sm font-medium h-8 rounded-md data-[state=inactive]:text-[#71717A] data-[state=active]:bg-white data-[state=active]:text-[#09090B] data-[state=active]:shadow-sm"
                        >
                            {t("recent")}
                        </TabsTrigger>
                        <TabsTrigger
                            value="all"
                            className="text-sm font-medium h-8 rounded-md data-[state=inactive]:text-[#71717A] data-[state=active]:bg-white data-[state=active]:text-[#09090B] data-[state=active]:shadow-sm"
                        >
                            {t("all")}
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Folder Table - with border and rounded corners */}
                <div className="flex-1 flex flex-col border border-[#E4E4E7] rounded-lg overflow-hidden min-h-0">
                    {/* Table Header - 48px height */}
                    <div className="flex items-center h-12 border-b border-[#E4E4E7] bg-white flex-shrink-0">
                        <div className="w-8 flex-shrink-0" />
                        <div className="w-[10px] flex-shrink-0" />
                        <span className="text-sm font-semibold text-[#71717A] px-2">
                            {t("folder-name")}
                        </span>
                    </div>

                    {/* Table Body - scrollable */}
                    <div className="flex-1 overflow-y-auto max-h-[272px]">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
                            </div>
                        ) : folders.length === 0 ? (
                            <div className="text-center py-8 text-[#71717A] text-sm font-medium">
                                {searchQuery ? t("no-folders-found") : t("no-folders")}
                            </div>
                        ) : (
                            <div>
                                {folders.map((folder, index) =>
                                    renderFolderRow(folder, 0, index === folders.length - 1)
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer - 50/50 width buttons */}
                <div className="flex items-center gap-3 flex-shrink-0">
                    {/* New Folder button - left half */}
                    <Button
                        variant="outline"
                        className="flex-1 h-9 flex items-center justify-center gap-2 font-bold text-sm"
                        onClick={() => setCurrentStep("createFolder")}
                    >
                        <Plus className="w-4 h-4" />
                        {t("new-folder")}
                    </Button>

                    {/* Action button - right half */}
                    <Button
                        variant="default"
                        onClick={handleProceedToNameStep}
                        disabled={!selectedFolderId}
                        className={cn(
                            "flex-1 h-9 font-bold text-sm",
                            !selectedFolderId && "opacity-50"
                        )}
                    >
                        {t("continue")}
                    </Button>
                </div>
            </AlertDialogContent>
        </AlertDialog>
    );
}
